import React, { useEffect, useMemo, useState } from 'react';
import { message } from 'antd';
import { FiBell, FiCheck, FiEdit2, FiX } from 'react-icons/fi';
import api from '../../../../system/client/src/services/api';
import Modal from '../../../Components/Modal';
import { ACTIVITY_TYPES, typeInfo, isOverdue, fmtWhen } from '../../../Components/activityTypes';
import './ActivityPanel.css';

// Local 'YYYY-MM-DDTHH:mm' for a datetime-local input.
const toLocalInput = (d) => {
    const pad = (n) => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

const tomorrowNine = () => {
    const d = new Date();
    d.setDate(d.getDate() + 1);
    d.setHours(9, 0, 0, 0);
    return toLocalInput(d);
};

const PLACEHOLDER = {
    note: 'Add a note about this trader…',
    call: 'Who did you speak to, and what came of it?',
    meeting: 'Who was there, and what was agreed?',
};

const FILTERS = [
    { key: 'all', label: 'All' },
    ...ACTIVITY_TYPES.map((t) => ({ key: t.key, label: t.plural, Icon: t.Icon })),
];

/* The right-hand panel on the trader page: log a note, call or meeting,
 * optionally with a reminder, and see the team's history for this trader.
 * Adding the first entry to an unsaved trader saves it as a Prospect
 * (ensureSaved) — the server only takes activities for saved traders. */
const ActivityPanel = ({ traderId, ensureSaved }) => {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [filter, setFilter] = useState('all');

    const [type, setType] = useState('note');
    const [body, setBody] = useState('');
    const [remind, setRemind] = useState(false);
    const [remindAt, setRemindAt] = useState(tomorrowNine);
    const [saving, setSaving] = useState(false);
    const [confirming, setConfirming] = useState(null); // activity awaiting delete
    const [deleting, setDeleting] = useState(false);
    // Editing one entry in place (FREIGHTSOU-2): { id, type, body, remind, remindAt }.
    const [editing, setEditing] = useState(null);
    const [savingEdit, setSavingEdit] = useState(false);

    useEffect(() => {
        let cancelled = false;
        setLoading(true);
        api.get(`traders/${traderId}/activities`)
            .then((rows) => { if (!cancelled) setItems(rows); })
            .catch((e) => console.error('Error loading activity:', e))
            .finally(() => { if (!cancelled) setLoading(false); });
        return () => { cancelled = true; };
    }, [traderId]);

    const counts = useMemo(() => {
        const c = { all: items.length };
        ACTIVITY_TYPES.forEach((t) => { c[t.key] = 0; });
        items.forEach((a) => {
            c[a.type] = (c[a.type] || 0) + 1;
        });
        return c;
    }, [items]);

    const shown = useMemo(() => {
        if (filter === 'all') return items;
        return items.filter((a) => a.type === filter);
    }, [items, filter]);

    const onSubmit = async (e) => {
        e.preventDefault();
        const text = body.trim();
        if (!text) {
            message.error('Please write something first.');
            return;
        }
        if (remind && !remindAt) {
            message.error('Pick a date and time for the reminder.');
            return;
        }
        setSaving(true);
        try {
            if (!(await ensureSaved())) return;
            const created = await api.post(`traders/${traderId}/activities`, {
                type,
                body: text,
                remindAt: remind ? new Date(remindAt).toISOString() : null,
            });
            setItems((prev) => [created, ...prev]);
            setBody('');
            setRemind(false);
            setRemindAt(tomorrowNine());
            message.success(`${typeInfo(type).label} added`);
        } catch (err) {
            message.error(err.response?.data?.error || 'Could not save that');
        } finally {
            setSaving(false);
        }
    };

    const toggleDone = async (a) => {
        try {
            const updated = await api.patch(`activities/${a.id}`, { done: !a.done_at });
            setItems((prev) => prev.map((x) => (x.id === a.id ? updated : x)));
        } catch {
            message.error('Could not update the reminder');
        }
    };

    const startEdit = (a) => setEditing({
        id: a.id,
        type: a.type,
        body: a.body,
        remind: Boolean(a.remind_at),
        remindAt: a.remind_at ? toLocalInput(new Date(a.remind_at)) : tomorrowNine(),
    });

    const saveEdit = async (e) => {
        e.preventDefault();
        const text = editing.body.trim();
        if (!text) {
            message.error('Please write something first.');
            return;
        }
        if (editing.remind && !editing.remindAt) {
            message.error('Pick a date and time for the reminder.');
            return;
        }
        setSavingEdit(true);
        try {
            const updated = await api.patch(`activities/${editing.id}`, {
                type: editing.type,
                body: text,
                remindAt: editing.remind ? new Date(editing.remindAt).toISOString() : null,
            });
            setItems((prev) => prev.map((x) => (x.id === updated.id ? updated : x)));
            setEditing(null);
            message.success('Saved');
        } catch (err) {
            message.error(err.response?.data?.error || 'Could not save that');
        } finally {
            setSavingEdit(false);
        }
    };

    const remove = async () => {
        const a = confirming;
        if (!a) return;
        setDeleting(true);
        try {
            await api.delete(`activities/${a.id}`);
            setItems((prev) => prev.filter((x) => x.id !== a.id));
            setConfirming(null);
        } catch {
            message.error('Could not delete that');
        } finally {
            setDeleting(false);
        }
    };

    const confirmLabel = confirming ? typeInfo(confirming.type).label.toLowerCase() : '';

    return (
        <div className="activity-panel">
            <span className="kicker">Activity</span>

            <form className="activity-compose" onSubmit={onSubmit}>
                <div className="activity-type-pick" role="radiogroup" aria-label="What are you logging?">
                    {ACTIVITY_TYPES.map(({ key, label, Icon }) => (
                        <button
                            key={key}
                            type="button"
                            role="radio"
                            aria-checked={type === key}
                            className={type === key ? 'on' : ''}
                            onClick={() => setType(key)}
                        >
                            <Icon aria-hidden="true" /> {label}
                        </button>
                    ))}
                </div>
                <textarea
                    value={body}
                    onChange={(e) => setBody(e.target.value)}
                    placeholder={PLACEHOLDER[type]}
                    maxLength={5000}
                    rows={3}
                />
                <div className="activity-remind">
                    <label className="activity-remind-toggle">
                        <input
                            type="checkbox"
                            checked={remind}
                            onChange={(e) => setRemind(e.target.checked)}
                        />
                        <FiBell aria-hidden="true" /> Remind me
                    </label>
                    {remind && (
                        <input
                            type="datetime-local"
                            value={remindAt}
                            onChange={(e) => setRemindAt(e.target.value)}
                            aria-label="Reminder date and time"
                        />
                    )}
                </div>
                <button type="submit" className="btn btn-primary btn-block" disabled={saving}>
                    {saving ? 'Saving…' : `Add ${typeInfo(type).label.toLowerCase()}`}
                </button>
            </form>

            <div className="activity-filters" role="group" aria-label="Show">
                {FILTERS.map(({ key, label, Icon }) => (
                    <button
                        key={key}
                        type="button"
                        className={filter === key ? 'on' : ''}
                        onClick={() => setFilter(key)}
                        disabled={key !== 'all' && !counts[key] && filter !== key}
                    >
                        {Icon && <Icon aria-hidden="true" />}
                        {label}
                        <span className="count">{counts[key] || 0}</span>
                    </button>
                ))}
            </div>

            <div className="activity-list">
                {loading ? (
                    <p className="activity-empty">Loading…</p>
                ) : shown.length === 0 ? (
                    <p className="activity-empty">
                        {items.length === 0
                            ? 'Nothing logged yet. Notes, calls and meetings you add show here for your whole team.'
                            : 'Nothing here.'}
                    </p>
                ) : (
                    shown.map((a) => {
                        const { Icon, label } = typeInfo(a.type);
                        const overdue = isOverdue(a);
                        if (editing && editing.id === a.id) {
                            const set = (k) => (v) => setEditing((d) => ({ ...d, [k]: v }));
                            return (
                                <form key={a.id} className="activity-compose activity-edit" onSubmit={saveEdit}>
                                    <div className="activity-type-pick" role="radiogroup" aria-label="What is this?">
                                        {ACTIVITY_TYPES.map(({ key, label: l, Icon: I }) => (
                                            <button
                                                key={key}
                                                type="button"
                                                role="radio"
                                                aria-checked={editing.type === key}
                                                className={editing.type === key ? 'on' : ''}
                                                onClick={() => set('type')(key)}
                                            >
                                                <I aria-hidden="true" /> {l}
                                            </button>
                                        ))}
                                    </div>
                                    <textarea
                                        value={editing.body}
                                        onChange={(e) => set('body')(e.target.value)}
                                        placeholder={PLACEHOLDER[editing.type]}
                                        maxLength={5000}
                                        rows={5}
                                        autoFocus
                                    />
                                    <div className="activity-remind">
                                        <label className="activity-remind-toggle">
                                            <input
                                                type="checkbox"
                                                checked={editing.remind}
                                                onChange={(e) => set('remind')(e.target.checked)}
                                            />
                                            <FiBell aria-hidden="true" /> Remind me
                                        </label>
                                        {editing.remind && (
                                            <input
                                                type="datetime-local"
                                                value={editing.remindAt}
                                                onChange={(e) => set('remindAt')(e.target.value)}
                                                aria-label="Reminder date and time"
                                            />
                                        )}
                                    </div>
                                    <div className="activity-edit-actions">
                                        <button type="button" className="btn btn-secondary" onClick={() => setEditing(null)} disabled={savingEdit}>
                                            Cancel
                                        </button>
                                        <button type="submit" className="btn btn-primary" disabled={savingEdit}>
                                            {savingEdit ? 'Saving…' : 'Save'}
                                        </button>
                                    </div>
                                </form>
                            );
                        }
                        return (
                            <article key={a.id} className={`activity-item type-${a.type}`}>
                                <span className="activity-icon" title={label}>
                                    <Icon aria-label={label} />
                                </span>
                                <div className="activity-main">
                                    <div className="activity-meta">
                                        <strong>{a.user_name}</strong>
                                        <span>{fmtWhen(a.created_at)}</span>
                                    </div>
                                    <div className="activity-body">{a.body}</div>
                                    {a.remind_at && (
                                        <button
                                            type="button"
                                            className={`activity-reminder${a.done_at ? ' done' : overdue ? ' overdue' : ''}`}
                                            onClick={() => toggleDone(a)}
                                            title={a.done_at ? 'Mark as not done' : 'Mark as done'}
                                        >
                                            <span className="tick" aria-hidden="true">
                                                {a.done_at && <FiCheck />}
                                            </span>
                                            {a.done_at ? 'Done' : overdue ? 'Overdue' : 'Remind'} · {fmtWhen(a.remind_at)}
                                        </button>
                                    )}
                                </div>
                                <div className="activity-item-actions">
                                    <button
                                        type="button"
                                        className="activity-edit-btn"
                                        aria-label={`Edit ${label.toLowerCase()}`}
                                        title="Edit — add what was said, change the type or the reminder"
                                        onClick={() => startEdit(a)}
                                    >
                                        <FiEdit2 />
                                    </button>
                                    <button
                                        type="button"
                                        className="activity-delete"
                                        aria-label={`Delete ${label.toLowerCase()}`}
                                        onClick={() => setConfirming(a)}
                                    >
                                        <FiX />
                                    </button>
                                </div>
                            </article>
                        );
                    })
                )}
            </div>

            <Modal
                open={Boolean(confirming)}
                onClose={() => !deleting && setConfirming(null)}
                kicker="Delete"
                title={`Delete this ${confirmLabel}?`}
                width={420}
                footer={
                    <>
                        <button
                            type="button"
                            className="btn btn-secondary"
                            onClick={() => setConfirming(null)}
                            disabled={deleting}
                        >
                            Cancel
                        </button>
                        <button
                            type="button"
                            className="btn btn-primary"
                            onClick={remove}
                            disabled={deleting}
                        >
                            {deleting ? 'Deleting…' : 'Delete'}
                        </button>
                    </>
                }
            >
                <p style={{ margin: 0, fontSize: 14, color: 'var(--ink-2)' }}>
                    It will be removed for everyone in your team
                    {confirming?.remind_at && !confirming?.done_at ? ', along with its reminder' : ''}.
                    This can't be undone.
                </p>
            </Modal>
        </div>
    );
};

export default ActivityPanel;
